import { AxiosError } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { finalize } from "rxjs";
import { getFormValidation } from "src/Shared/Components";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { useStore } from "src/Shared/Hooks";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { CommonService, ToastService } from "src/Shared/Services";
import { ILinkImperansCompany } from "../../Interfaces/ILinkImperansCompany";
import CompanyService from "../../Services/Company.service";
import { fields } from "./fields";
import { IRouteParams } from "../../Interfaces";
import { useParams } from "react-router-dom";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Tooltip,
} from "@mui/material";
import { useStyles } from "./CompanyIntegration.styles";
import { observer } from "mobx-react";

/** What Save Changes is waiting for while it sits disabled (bug 5842). */
const SAVE_NEEDS_COMPANY = "Choose an Imperans company before saving.";

const CompanyIntegration = () => {
  const {
    rootStore: {
      companyStore: {
        getCompanyImperansIntegration,
        companyImperansIntegration,
        getCompanyOverview,
        companyOverview,
      },
    },
  } = useStore();

  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const { companyRowID } = useParams<Partial<IRouteParams>>();

  const companyIntergrationForm = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => companyIntergrationForm.$(key);

  useEffect(() => {
    getCompanyImperansIntegration(companyRowID);
  }, []);

  useEffect(() => {
    if (companyImperansIntegration) {
      const imperansCompanyField = getField("imperansCompany");
      imperansCompanyField.set(
        "value",
        companyImperansIntegration.companyIntegrationDetail,
      );
    }
  }, [companyImperansIntegration]);

  const gridProps: IGridFormControlProps = {
    row: 1,
    col: 1,
    itemResponsive: {
      lg: 12,
      sm: 12,
      md: 4,
    },
    inputControls: [
      {
        field: getField("imperansCompany"),
        fieldKey: "imperansCompany",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
        apiObservable: (search: string) =>
          CommonService.getImperansCompanies(search, 20),
        rows: 12,
        hideValueInOptions: true,
        stickyOptions: [
          {
            label: "Company does not exist in Imperans",
            value: 0,
          },
        ],
      },
    ],
    form: companyIntergrationForm,
  };

  /**
   * Whether there is a company in the dropdown for Save Changes to send.
   *
   * Read off the live field rather than the stored match, because this gates the button that does
   * the storing — the point is that a selection made now enables it now. The component is an
   * observer, so the button re-enables on selection without anything else firing. Note that
   * "Company does not exist in Imperans" counts as a selection: it is a deliberate answer that
   * saves its own integration status, not an empty field.
   */
  const hasCompanySelected = Boolean(getField("imperansCompany").value);

  const onSubmitForm = (): any => {
    if (!companyIntergrationForm.isValid) {
      // A backstop: the button is disabled for this case, so nothing should reach here by clicking
      // it. If some other path ever calls this, refuse out loud rather than appearing to save —
      // showErrors() alone only reddens a field that sits below the header the button lives in.
      companyIntergrationForm.showErrors();
      ToastService.error(SAVE_NEEDS_COMPANY);
      return;
    }
    const request: ILinkImperansCompany = {
      companyRowID: companyRowID,
      imperansCompanyID: companyIntergrationForm.values().imperansCompany.value,
    };
    setLoading(true);
    CompanyService.linkCompanyToImperans(request)
      .pipe(
        finalize(() => {
          setLoading(false);
        }),
      )
      .subscribe({
        next: (response) => {
          ToastService.success(response.message);
          getCompanyImperansIntegration(companyRowID);
          getCompanyOverview(companyRowID);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };
  const isLinked =
    companyOverview?.companyIntegrationStatus === "Linked to Imperans";

  // The flag saves on its own the moment it is ticked, independently of the match below it.
  const onLinkFlagChange = (checked: boolean) => {
    CompanyService.setImperansLinkFlag(companyRowID, checked).subscribe({
      next: (response) => {
        ToastService.success(response.message);
        // The Overview's Imperans Integration line updates immediately (CR.7),
        // silently — a flag toggle must not blank the record behind a loader.
        getCompanyOverview(companyRowID, true);
      },
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  return (
    <>
      <Box className={classes.subMenuHeader}>
        <Typography className={classes.title} variant="h5">
          Imperans Link
        </Typography>
        {/* Disabled until a company is chosen, since there is nothing to save without one. The
            tooltip carries the reason: a greyed-out button with no explanation is the same dead
            end as a click that silently does nothing, and it needs the span because a disabled
            button fires no events for the tooltip to hang off. */}
        <Tooltip title={hasCompanySelected ? "" : SAVE_NEEDS_COMPANY}>
          <span>
            <Button
              variant="contained"
              onClick={onSubmitForm}
              disabled={!hasCompanySelected}
            >
              Save Changes
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
        {/* Two independent things live on this page: the Linked to Imperans flag below (saves the
            instant it is ticked, deliberately) and the Imperans Company match above (saves only on
            Save Changes, which stays disabled until one is chosen). The flag was reported as
            "succeeding" while this line still read "not yet matched" — both were accurate at once,
            since the flag says nothing about whether a specific company has been matched, but read
            as a contradiction with nothing on screen explaining it (bug 5842). Naming the
            flagged-but-unmatched state says what actually happened instead of leaving the two
            readings to argue with each other. */}
        <Typography variant="body2">
          {companyImperansIntegration?.companyIntegrationDetail
            ? `This client exists in Imperans as "${companyImperansIntegration.companyIntegrationDetail.label}".`
            : isLinked
              ? "Linked to Imperans, but not yet matched to a specific Imperans company."
              : "This client is not yet matched to an Imperans company."}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={isLinked}
              onChange={(e) => onLinkFlagChange(e.target.checked)}
            />
          }
          label="Linked to Imperans"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          The flag stands in for the Imperans code index in the company-code
          uniqueness check until the Imperans lookup endpoint is connected. In
          phase 2, submitting an onboarding will create the client in Imperans
          automatically.
        </Typography>
      </Paper>

      <GridFormControl {...gridProps} />
    </>
  );
};

export default observer(CompanyIntegration);
